import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getReviewQueue, approveTransaction, rejectTransaction, getAccounts } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Check,
  X,
  Brain,
  Buildings,
  Warning,
  CaretRight
} from '@phosphor-icons/react';

function ConfidenceBadge({ score }) {
  if (!score && score !== 0) return <span className="text-xs text-zinc-400">—</span>;
  
  if (score >= 80) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${score}%` }}></div>
        </div>
        <span className="text-xs font-mono text-emerald-600 w-10">{score}%</span>
      </div>
    );
  } else if (score >= 50) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full" style={{ width: `${score}%` }}></div>
        </div>
        <span className="text-xs font-mono text-amber-600 w-10">{score}%</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-red-500 rounded-full" style={{ width: `${score}%` }}></div>
      </div>
      <span className="text-xs font-mono text-red-600 w-10">{score}%</span>
    </div>
  );
}

export default function ReviewQueuePage() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [queueRes, accountsRes] = await Promise.all([
        getReviewQueue(),
        getAccounts()
      ]);
      setTransactions(queueRes.data.transactions);
      setAccounts(accountsRes.data.accounts);
      if (queueRes.data.transactions.length > 0) {
        setSelectedTxn(queueRes.data.transactions[0]);
      }
    } catch (error) {
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedTxn) return;
    setProcessing(true);
    try {
      await approveTransaction(selectedTxn.transaction_id, {
        account_id: selectedAccount || selectedTxn.ai_analysis?.suggested_account
      });
      toast.success('Transaction approved and posted');
      // Move to next transaction
      const currentIndex = transactions.findIndex(t => t.transaction_id === selectedTxn.transaction_id);
      const newTransactions = transactions.filter(t => t.transaction_id !== selectedTxn.transaction_id);
      setTransactions(newTransactions);
      if (newTransactions.length > 0) {
        setSelectedTxn(newTransactions[Math.min(currentIndex, newTransactions.length - 1)]);
      } else {
        setSelectedTxn(null);
      }
      setSelectedAccount('');
    } catch (error) {
      toast.error('Failed to approve transaction');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTxn) return;
    setProcessing(true);
    try {
      await rejectTransaction(selectedTxn.transaction_id, {
        reason: rejectionReason
      });
      toast.success('Transaction rejected');
      // Move to next transaction
      const currentIndex = transactions.findIndex(t => t.transaction_id === selectedTxn.transaction_id);
      const newTransactions = transactions.filter(t => t.transaction_id !== selectedTxn.transaction_id);
      setTransactions(newTransactions);
      if (newTransactions.length > 0) {
        setSelectedTxn(newTransactions[Math.min(currentIndex, newTransactions.length - 1)]);
      } else {
        setSelectedTxn(null);
      }
      setRejectionReason('');
    } catch (error) {
      toast.error('Failed to reject transaction');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Review Queue">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Review Queue">
      <div data-testid="review-queue-page" className="h-[calc(100vh-12rem)]">
        {transactions.length === 0 ? (
          <div className="flex items-center justify-center h-full text-zinc-500">
            <div className="text-center">
              <Check size={48} className="mx-auto mb-4 text-emerald-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No transactions require review</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6 h-full">
            {/* Left Panel - Transaction List */}
            <div className="col-span-1 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                <p className="text-xs uppercase tracking-widest text-zinc-500">
                  {transactions.length} Transactions Pending Review
                </p>
              </div>
              <div className="flex-1 overflow-auto">
                {transactions.map((txn) => (
                  <button
                    key={txn.transaction_id}
                    onClick={() => {
                      setSelectedTxn(txn);
                      setSelectedAccount('');
                      setRejectionReason('');
                    }}
                    data-testid={`queue-item-${txn.transaction_id}`}
                    className={`w-full text-left px-4 py-4 border-b border-zinc-100 transition-colors duration-150 ${
                      selectedTxn?.transaction_id === txn.transaction_id
                        ? 'bg-zinc-100 border-l-4 border-l-zinc-950'
                        : 'hover:bg-zinc-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-sm truncate flex-1">{txn.vendor_name}</p>
                      <CaretRight size={16} className="text-zinc-400 flex-shrink-0 ml-2" />
                    </div>
                    <p className="text-xs text-zinc-500 mb-2 truncate">{txn.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">
                        ${Math.abs(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                      {txn.ai_analysis?.is_sensitive && (
                        <Warning size={16} className="text-amber-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Panel - Detail View */}
            <div className="col-span-2 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
              {selectedTxn ? (
                <>
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
                    <h2 className="font-semibold">{selectedTxn.vendor_name}</h2>
                    <p className="text-sm text-zinc-500">{selectedTxn.date}</p>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-auto p-6 space-y-6">
                    {/* 3-Line Trust Display */}
                    {selectedTxn.ai_analysis?.pattern_summary && (
                      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="review-trust-display">
                        <div className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-violet-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <div>
                            <p className="text-xs uppercase tracking-widest text-violet-600">Suggested by Pattern</p>
                            <p className="text-sm text-zinc-800">{selectedTxn.ai_analysis.pattern_summary}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <div>
                            <p className="text-xs uppercase tracking-widest text-blue-600">Checked Against Company Policy</p>
                            <p className="text-sm text-zinc-800">{selectedTxn.ai_analysis.policy_check}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                            selectedTxn.ai_analysis.requires_review ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}></span>
                          <div>
                            <p className={`text-xs uppercase tracking-widest ${
                              selectedTxn.ai_analysis.requires_review ? 'text-amber-600' : 'text-emerald-600'
                            }`}>Why Review Is Needed</p>
                            <p className="text-sm text-zinc-800">{selectedTxn.ai_analysis.review_reason_display}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Transaction Info */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Amount</p>
                        <p className="text-2xl font-mono font-bold">
                          ${Math.abs(selectedTxn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">AI Confidence</p>
                        <div className="mt-2">
                          <ConfidenceBadge score={selectedTxn.ai_analysis?.confidence_score} />
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Description</p>
                      <p className="text-sm">{selectedTxn.description}</p>
                    </div>

                    {selectedTxn.memo && (
                      <div>
                        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Memo</p>
                        <p className="text-sm text-zinc-600">{selectedTxn.memo}</p>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {selectedTxn.ai_analysis && (
                      <div className="space-y-4">
                        {/* Pattern Recognition */}
                        <div className="bg-violet-50 border border-violet-200 p-4 rounded-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <Brain size={18} weight="duotone" className="text-violet-600" />
                            <p className="text-xs uppercase tracking-widest text-violet-600">Pattern Recognition</p>
                          </div>
                          {selectedTxn.ai_analysis.pattern_signals && (
                            <p className="text-sm text-violet-900 mb-2">
                              {typeof selectedTxn.ai_analysis.pattern_signals === 'string' 
                                ? selectedTxn.ai_analysis.pattern_signals 
                                : JSON.stringify(selectedTxn.ai_analysis.pattern_signals)}
                            </p>
                          )}
                          {selectedTxn.ai_analysis.suggested_account && (
                            <p className="text-sm">
                              <span className="text-violet-600">Suggested:</span>{' '}
                              <span className="font-medium">{selectedTxn.ai_analysis.suggested_account}</span>
                            </p>
                          )}
                        </div>

                        {/* Business Context */}
                        {(selectedTxn.ai_analysis.context_flags || selectedTxn.ai_analysis.review_reason) && (
                          <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
                            <div className="flex items-center gap-2 mb-3">
                              <Buildings size={18} weight="duotone" className="text-blue-600" />
                              <p className="text-xs uppercase tracking-widest text-blue-600">Business Context</p>
                            </div>
                            {selectedTxn.ai_analysis.context_flags && (
                              <p className="text-sm text-blue-900 mb-2">
                                {typeof selectedTxn.ai_analysis.context_flags === 'string'
                                  ? selectedTxn.ai_analysis.context_flags
                                  : JSON.stringify(selectedTxn.ai_analysis.context_flags)}
                              </p>
                            )}
                            {selectedTxn.ai_analysis.review_reason && (
                              <p className="text-sm text-blue-900">
                                <span className="font-medium">Review required:</span> {selectedTxn.ai_analysis.review_reason}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Sensitive Flag */}
                        {selectedTxn.ai_analysis.is_sensitive && (
                          <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <Warning size={18} weight="duotone" className="text-amber-600" />
                              <p className="text-xs uppercase tracking-widest text-amber-600">Sensitive Transaction</p>
                            </div>
                            <p className="text-sm text-amber-900">
                              Category: <span className="font-medium">{selectedTxn.ai_analysis.sensitive_category || 'Unknown'}</span>
                            </p>
                          </div>
                        )}

                        {/* Explanation */}
                        {selectedTxn.ai_analysis.explanation && (
                          <div>
                            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">AI Explanation</p>
                            <p className="text-sm text-zinc-600 leading-relaxed">{selectedTxn.ai_analysis.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Account Selection */}
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Assign Account</p>
                      <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                        <SelectTrigger className="w-full rounded-sm" data-testid="account-select">
                          <SelectValue placeholder={selectedTxn.ai_analysis?.suggested_account || "Select an account"} />
                        </SelectTrigger>
                        <SelectContent>
                          {accounts.map((acc) => (
                            <SelectItem key={acc.account_id} value={acc.account_id}>
                              {acc.code} - {acc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Rejection Reason */}
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Rejection Reason (if rejecting)</p>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this transaction is being rejected..."
                        className="rounded-sm resize-none"
                        rows={3}
                        data-testid="rejection-reason-input"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={handleReject}
                      disabled={processing}
                      className="rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="reject-transaction-button"
                    >
                      <X size={18} className="mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={handleApprove}
                      disabled={processing}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="approve-transaction-button"
                    >
                      <Check size={18} className="mr-2" />
                      Approve & Post
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-zinc-500">
                  <p>Select a transaction to review</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
